<script setup lang="ts">
import { XIcon, XSkeletonLoader } from '@clicksign/excalibur';
import { useToast } from 'vue-toastification';
import { useQueryClient } from '@tanstack/vue-query';

import { isFetchError, Decisions, useFeature } from '~~core';
import { useFetchRubricates, saveRubricates } from '~~document';

import SignaturePad from '../../../../components/SignaturePad/SignaturePad.vue';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { requirementQueryKeys, useFetchRequirement } from '../../../../repositories/requirement';
import { saveRequirement } from '../../../../services/requirement';
import { useRequirement } from '../../../../composables/use-requirement';
import { RequirementAdvanceButton } from '../../../../components';

const STEP_NAME = 'handwritten';

const queryClient = useQueryClient();
const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();
const toast = useToast();
const { t } = useI18n();
const { requirement, isLoading } = useFetchRequirement(STEP_NAME, signatureRequestKey);
const { rubricates, refetch: refetchRubricates } = useFetchRubricates(signatureRequestKey, 'manuscript');
const { evidence: savedEvidence, isFilled: isReadonly } = useRequirement(requirement);
const title = t('requirement.handwritten.title');

const { isEnabled: isReusableManuscriptEnabled } = useFeature(Decisions.REUSABLE_MANUSCRIPT);

useHead({ title });
setActiveRequirement(STEP_NAME);

const signatureBlob = ref<Blob>();
const isSaving = ref(false);

const reusableArtefact = computed(() => rubricates.value?.manuscript || undefined);
const changedArtefact = ref(false);
const hasReusableArtefact = computed(() => isReusableManuscriptEnabled.value && !!reusableArtefact.value && !changedArtefact.value);

const isDisabled = computed(() => !signatureBlob.value && !isReadonly.value && !hasReusableArtefact.value);

const filledArtefact = computed(() => {
  if (isReadonly.value) {
    return savedEvidence.value?.artefact?.imageUrl;
  }

  return hasReusableArtefact.value ? reusableArtefact.value : undefined;
});

function handleSignaturePadChange(blob: Blob | undefined) {
  changedArtefact.value = true;
  signatureBlob.value = blob;
}

async function advanceStep() {
  if (isReadonly.value) return finishRequirement();
  isSaving.value = true;

  try {
    if (isReusableManuscriptEnabled.value) {
      if (!hasReusableArtefact.value) {
        if (!signatureBlob.value) throw new Error('Signature Blob not collected');
        const rubricate = { manuscript: signatureBlob.value };

        await saveRubricates(signatureRequestKey.value, { rubricate });
        await refetchRubricates();
      }
      if (!rubricates.value) throw new Error('rubricated not collected');

      await saveRequirement(STEP_NAME, signatureRequestKey.value, {
        rubricateKey: rubricates.value?.key,
      }, undefined, { skipFormData: true });

      await queryClient.invalidateQueries(requirementQueryKeys.show(signatureRequestKey.value, STEP_NAME));
      finishRequirement();
    } else {
      if (!signatureBlob.value) throw new Error('Signature Blob not collected');
      await saveRequirement(STEP_NAME, signatureRequestKey.value, {
        image: signatureBlob.value,
      });

      await queryClient.invalidateQueries(requirementQueryKeys.show(signatureRequestKey.value, STEP_NAME));
      finishRequirement();
    }
  } catch (error) {
    isSaving.value = false;
    toast.error(t('requirement.handwritten.errors.save'));

    if (!isFetchError(error)) throw error;
  }
}

watch(() => hasReusableArtefact.value, (hasArtefact) => {
  if (hasArtefact) {
    toast.info(t('requirement.handwritten.hasReusableArtefact'));
  }
});
</script>

<template>
  <div
    v-if="isLoading"
    :class="$style.root"
  >
    <h3 :class="$style.textCenter">
      <XSkeletonLoader
        height="32px"
        rounded
      />
    </h3>

    <XSkeletonLoader
      height="auto"
      :class="$style.padSkeleton"
      rounded
    />

    <XSkeletonLoader
      :class="$style.disclaimer"
      height="1em"
      rounded
    />

    <div :class="$style.actions">
      <XSkeletonLoader
        height="4.8rem"
        rounded
      />

      <XSkeletonLoader
        height="4.8rem"
        rounded
      />
    </div>
  </div>

  <div
    v-else
    :class="$style.root"
  >
    <h3 :class="$style.textCenter">
      {{ title }}
    </h3>

    <div :class="$style.padWrapper">
      <SignaturePad
        ref="signaturePad"
        :class="$style.pad"
        :readonly="isReadonly"
        :img-url="filledArtefact"
        @change="handleSignaturePadChange"
      />
    </div>

    <small :class="$style.disclaimer">
      <XIcon
        icon="info-circle"
        size="lg"
        left
      />
      {{ t('requirement.handwritten.disclaimer') }}
    </small>

    <div :class="$style.actions">
      <RequirementAdvanceButton
        full-width
        :loading="isSaving"
        :disabled="isDisabled || isLoading"
        @click="advanceStep"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.textCenter {
  text-align: center;
}

.root {
  width: 100%;
  max-width: var(--step-max-width);
  margin: 0 auto;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-block: var(--space-regular);
  gap: var(--space-large);
}

.pad {
  width: 100%;
  background-color: var(--color-neutral-0);
}

.disclaimer {
  display: block;
  margin-top: var(--space-small-xx);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-800);
}

.padWrapper {
  position: relative;
  z-index: 0;
  margin-top: var(--space-large);
  background-color: var(--color-neutral-0);
  border-color: var(--color-neutral-400);
  border-radius: var(--border-radius-large);
}

.padSkeleton {
  aspect-ratio: 424 / 211;
}

.guide {
  position: absolute;
  z-index: -1;
  right: var(--space-regular);
  bottom: var(--space-medium);
  left: var(--space-regular);
  width: 100%;
  max-width: calc(100% - var(--space-large));
  pointer-events: none;

  .signatureLine {
    margin: 0;
    color: var(--color-neutral-700);
  }

  .icon {
    color: var(--color-neutral-700);
  }
}
</style>
